import styled, { css } from 'styled-components';
import { MOBILE_QUERY } from './DraftBoard.styles';

/* Phones, where this panel is DraftBoard's bottom sheet. */
const MOBILE = `@media only screen and ${MOBILE_QUERY}`;

/* ── Side panel (replaces centered modal) ─────────────────────────── */

export const SidePanel = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

export const YourPickOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 200;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

export const YourPickSheet = styled.div`
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
`;

export const YourPickHeader = styled.div`
  background: linear-gradient(90deg, #012337, #004e7b);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  flex-shrink: 0;
`;

export const YourPickTitle = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(1.5rem, 1.5vw, 2.6rem);
  font-weight: 900;
  color: #fff;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

export const YourPickCloseBtn = styled.button`
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;

  svg { display: block; }
`;

export const YourPickBody = styled.div`
  background: #fff;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const PlayerListScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

/* ── Info section ─────────────────────────────────────────────────── */

export const PickInfoSection = styled.div`
  padding: 1rem;
  flex-shrink: 0;
`;

/* ── Panel header ─────────────────────────────────────────────────── */

/* Clock banner, team and needs, and the tabs. On mobile this is the bottom sheet's header — what
   shows while the sheet is closed — and it takes the taps and swipes that open and close it.
   touch-action: none because React's touch listeners are passive and can't stop the browser
   scrolling or pull-to-refreshing mid-swipe (see useSheetSwipe). */
export const PanelHeader = styled.div`
  flex-shrink: 0;

  ${MOBILE} {
    touch-action: none;
    user-select: none;
  }
`;

/* ── Clock banner ─────────────────────────────────────────────────── */

/* Full-width status bar: green on the user's turn, slate while the simulator picks. Replaces the
   small pill — the turn is the single most important thing on the panel, so it gets the band. */
export const ClockBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: clamp(12px, 0.75vw, 29px);
  padding: clamp(7px, 0.44vw, 17px) clamp(16px, 1vw, 38px);
  flex-shrink: 0;
  background: ${({ $active }) => ($active ? '#3f8f54' : '#5b6b7a')};
  font-family: 'Saira Semi Condensed', sans-serif;
  color: #fff;

  ${MOBILE} {
    gap: 10px;
    padding: 10px 12px 10px 16px;
    cursor: pointer;
  }
`;

export const ClockBannerLeft = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(8px, 0.5vw, 19px);
  font-size: clamp(20px, 1.25vw, 48px);
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  min-width: 0;

  svg {
    display: block;
    flex-shrink: 0;
    width: clamp(16px, 1vw, 38px);
    height: clamp(16px, 1vw, 38px);
  }

  /* flex: 1 keeps the pick and the sheet toggle together on the right. */
  ${MOBILE} {
    flex: 1;
    font-size: 17px;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

export const ClockBannerPick = styled.div`
  font-size: clamp(15px, 0.94vw, 36px);
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  white-space: nowrap;
  opacity: 0.95;

  ${MOBILE} {
    font-size: 14px;
  }
`;

/* Mobile only: opens and closes the bottom sheet. Points up while the sheet is closed. */
export const SheetToggleBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: -6px -4px -6px 0;
  padding: 0;
  flex-shrink: 0;
  background: none;
  border: none;
  border-radius: 50%;
  color: #fff;
  cursor: pointer;

  &:focus-visible { outline: 2px solid #fff; outline-offset: -2px; }

  svg {
    width: 24px;
    height: 24px;
    transition: transform 0.28s ease;
    transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  }
`;

/* ── Team + needs row ─────────────────────────────────────────────── */

export const TeamNeedsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 16px;
  flex-shrink: 0;
  justify-content: space-between;
  background: #f0f1f3;
  min-height: 65px;

  ${MOBILE} {
    min-height: 58px;
    padding: 8px 16px;
    cursor: pointer;
  }
`;

export const TeamNeedsTeam = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  img {
    width: 40px;
    height: 40px;
    object-fit: contain;
    flex-shrink: 0;
  }
`;

export const TeamNeedsName = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(18px, 1.12vw, 43px);
  font-weight: 900;
  color: #012337;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const NeedsCard = styled.div`
  min-width: 0;
  background: #e4e5ea;
  border-radius: 8px;
  padding: 5px 12px;
`;

export const NeedsCardLabel = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(12px, 0.75vw, 29px);
  font-weight: 600;
  color: #6b7280;
  line-height: 1.3;
`;

export const NeedsCardValue = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 800;
  color: #012337;
  line-height: 1.35;
`;

export const PickInfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

/* Compact turn indicator, top-right of the panel. Replaces the old blocking overlay so the
   player list stays readable while the simulator picks — only the Draft buttons are disabled. */
export const TurnStatus = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  align-self: flex-start;
  padding: clamp(5px, 0.31vw, 12px) clamp(12px, 0.75vw, 29px);
  border-radius: 100px;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(13px, 0.81vw, 31px);
  font-weight: 700;
  white-space: nowrap;
  border: 1.5px solid ${({ $active }) => ($active ? '#1f8f4e' : '#cad2da')};
  background: ${({ $active }) => ($active ? '#e9f7ef' : '#f4f6f8')};
  color: ${({ $active }) => ($active ? '#12693a' : '#5b6b7a')};

  svg { display: block; }

  svg {
    width: clamp(13px, 0.81vw, 31px);
    height: clamp(13px, 0.81vw, 31px);
  }
`;

export const PickInfoLabel = styled.span`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 600;
  color: #000;
`;

export const PickInfoValue = styled.span`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 400;
  color: #3f3f3f;
`;

export const NeedsRow = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  color: #3f3f3f;
  line-height: 1.4;
`;

export const NeedsLabel = styled.span`
  font-weight: 600;
  color: #000;
`;

export const PositionLabel = styled.span`
  font-weight: 600;
  font-size: clamp(14px, 0.88vw, 34px);
  color: #000;
`;

/* ── Draft a Player / Trade tabs ──────────────────────────────────── */

export const PanelTabs = styled.div`
  display: flex;
  gap: clamp(8px, 0.5vw, 19px);
  padding: 0 16px;
  flex-shrink: 0;
  background: #f0f1f3; /* same as TeamNeedsRow above, so team + tabs read as one header */
  border-bottom: 1px solid #e5e7eb;
`;

export const PanelTab = styled.button`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(15px, 0.94vw, 36px);
  font-weight: 700;
  background: none;
  border: none;
  padding: clamp(10px, 0.62vw, 24px) clamp(8px, 0.5vw, 19px);
  margin-bottom: -1px;
  cursor: pointer;
  white-space: nowrap;
  color: ${({ $active }) => ($active ? '#012337' : '#50647b')};
  border-bottom: 3px solid ${({ $active }) => ($active ? '#004ea3' : 'transparent')};

  &:hover:not(:disabled) { color: #012337; }
  &:disabled { color: #b5bec8; cursor: not-allowed; }
`;

/* ── Position pill filter ─────────────────────────────────────────── */

export const PosFilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(6px, 0.38vw, 14px);
  padding: 0 1rem 1rem;
  overflow-x: auto;
  flex-shrink: 0;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

export const PosToggle = styled.button`
  height: clamp(28px, 1.75vw, 67px);
  padding: 0 clamp(12px, 0.75vw, 29px);
  border-radius: 100px;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(12px, 0.75vw, 29px);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  /* Even widths across the row rather than sizing to the label, so the pills
     line up. flex: 1 replaces flex-shrink: 0 — the two contradict. */
  flex: 1;
  max-width: clamp(50px, 3.13vw, 120px);
  padding: 0;
  /* $primary is the "All" pill: navy fill with a white label where the others use
     their position colour, so the reset reads as a state. At rest it is identical
     to every other pill — white fill, grey border, dark label. */
  border: 2px solid ${({ $active, $primary, $color }) =>
    $active && $primary ? '#012337' : $color || '#e2e8f0'};
  background: ${({ $active, $primary, $color }) =>
    $active ? ($primary ? '#012337' : $color || '#e2e8f0') : '#fff'};
  color: ${({ $active, $primary }) => ($active && $primary ? '#fff' : '#1a202c')};

  /* Hover previews the active state, the same way a position pill previews its
     own colour — so "All" goes navy with a white label whether or not it is on. */
  &:hover {
    background: ${({ $primary, $color }) => ($primary ? '#012337' : $color || '#e2e8f0')};
    border-color: ${({ $primary, $color }) => ($primary ? '#012337' : $color || '#e2e8f0')};
    color: ${({ $primary }) => ($primary ? '#fff' : '#1a202c')};
  }
`;

/* Mobile: the position filter is a multi-select dropdown instead of the pill row — thirteen
   pills don't fit a phone. MUI Select, so its menu portals out of the sheet. */
export const PosSelectWrap = styled.div`
  padding: 0 1rem 1rem;
  flex-shrink: 0;
`;

/* "All positions", in the closed dropdown and as the menu's first option. */
export const PosOption = styled.span`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #1a202c;
`;

/* The closed value: the chosen positions as colour chips (PosChip in YourPickModal), wrapping
   onto more lines rather than being cut off. */
export const PosSelectValue = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
`;

/* ── Search ───────────────────────────────────────────────────────── */

export const SearchWrap = styled.div`
  padding: 1rem 1rem 0.75rem;
  flex-shrink: 0;
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: clamp(8px, 0.5vw, 19px) clamp(14px, 0.9vw, 34px);
  border: 1.5px solid #cad2da;
  border-radius: 10px;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  color: #000;
  outline: none;
  box-sizing: border-box;

  &::placeholder { color: #9ca3af; }
  &:focus { border-color: #004ea3; }

  /* iOS zooms into an input under 16px on focus, which threw the bottom sheet off screen. */
  ${MOBILE} {
    font-size: 16px;
  }
`;

/* ── Table header ─────────────────────────────────────────────────── */

/* Header and rows lay out on ONE template so their columns cannot drift.
   Tracks: player | position | BPA | ADP | draft button. */
const playerTableGrid = css`
  display: grid;
  grid-template-columns:
    minmax(0, 1fr)
    clamp(48px, 3vw, 115px)
    clamp(48px, 3vw, 115px)
    clamp(56px, 3.5vw, 134px)
    clamp(78px, 5vw, 187px);
  align-items: center;
  column-gap: clamp(16px, 1vw, 38px);
  padding-left: clamp(16px, 1vw, 38px);
  padding-right: clamp(16px, 1vw, 38px);

  /* Every column kept, just tighter, so the player name still gets ~150px on a 390px phone. */
  ${MOBILE} {
    grid-template-columns: minmax(0, 1fr) 46px 34px 48px 60px;
    column-gap: 8px;
    padding-left: 12px;
    padding-right: 12px;
  }
`;

export const TableHeader = styled.div`
  ${playerTableGrid};
  padding-top: clamp(10px, 0.63vw, 24px);
  padding-bottom: clamp(10px, 0.63vw, 24px);
  background: #e2e2e2;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
`;

export const TableHeaderPlayer = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 800;
  color: #000;
  flex: 1;
`;


export const TableHeaderCell = styled.span`
  min-width: 0;
  text-align: center;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 800;
  color: #000;

  /* "Position" has to fit its 46px column. */
  ${MOBILE} {
    font-size: 12px;
  }
`;

/* ── Player rows ──────────────────────────────────────────────────── */

export const PlayerRow = styled.div`
  ${playerTableGrid};
  padding-top: clamp(10px, 0.63vw, 24px);
  padding-bottom: clamp(10px, 0.63vw, 24px);
  border-bottom: 1px solid #e5e7eb;
  min-height: clamp(72px, 4.5vw, 173px);
  background: ${({ $shaded }) => ($shaded ? '#f1f3f5' : 'transparent')};

  ${MOBILE} {
    min-height: 60px;
  }
`;

export const PlayerAvatar = styled.div`
  width: clamp(48px, 3.0vw, 115px);
  height: clamp(48px, 3.0vw, 115px);
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const PlayerAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const PlayerInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const PlayerName = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(15px, 0.94vw, 36px);
  font-weight: 700;
  color: #000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const PlayerSchool = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(13px, 0.81vw, 31px);
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const PositionBadge = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(13px, 0.81vw, 31px);
  font-weight: 700;
  color: #000;
  background: ${({ $color }) => $color || '#e5e7eb'};
  border-radius: 100px;
  padding: clamp(3px, 0.2vw, 8px) 4px;
  flex-shrink: 0;
  width: 100%;
  overflow: hidden;
  text-align: center;
`;

export const PlayerRank = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(15px, 0.94vw, 36px);
  font-weight: 600;
  color: #000;
  min-width: 0;
  text-align: center;
  flex-shrink: 0;
`;

export const PlayerAdp = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(15px, 0.94vw, 36px);
  font-weight: 600;
  color: #000;
  min-width: 0;
  text-align: center;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
`;

export const DraftPickBtn = styled.button`
  padding: clamp(8px, 0.5vw, 19px) clamp(18px, 1.15vw, 43px);
  background: #012337;
  color: #fff;
  border: none;
  border-radius: 100px;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 700;
  cursor: pointer;
  min-width: 0;
  width: 100%;

  &:hover:not(:disabled) { background: #03111b; }
  &:active:not(:disabled) { background: #000; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }

  ${MOBILE} {
    padding: 8px 0;
  }
`;

/* ── Before the draft starts ──────────────────────────────────────── */

/* The Draft a Player tab's content — filters, search and the table — as one block, so the
   stripes below can sit over all of it without scrolling away with the list. */
export const DraftTabArea = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

/* Striped overlay on the Draft a Player tab until the draft starts, with "Ready to draft?" and
   Start at its centre. It used to cover the whole panel, tabs included. Like that one it blocks
   clicks, so nothing under it can be picked early. */
export const DraftTabStripes = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(10px, 0.62vw, 24px);
  padding: 24px;
  text-align: center;
  backdrop-filter: blur(1px);
  background: repeating-linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.55) 0px,
    rgba(255, 255, 255, 0.55) 14px,
    rgba(238, 242, 245, 0.55) 14px,
    rgba(238, 242, 245, 0.55) 28px
  );
`;

export const StartBarText = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-weight: 800;
  font-size: clamp(18px, 1.12vw, 43px);
  color: #012337;
`;

export const StartBarBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(10px, 0.63vw, 24px);
  padding: 0 clamp(28px, 1.75vw, 67px);
  height: clamp(48px, 3vw, 115px);
  background: #16a34a;
  border: none;
  border-radius: 100px;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(16px, 1.0vw, 38px);
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 4px 14px rgba(22, 163, 74, 0.35);
  transition: background 0.15s, box-shadow 0.15s;

  &:hover { background: #15803d; box-shadow: 0 6px 18px rgba(22, 163, 74, 0.4); }
  &:active { background: #166534; }

  svg {
    width: clamp(12px, 0.75vw, 29px);
    height: clamp(14px, 0.88vw, 34px);
  }
`;

/* Mobile only (rendered when the panel is a bottom sheet): Start Draft across the closed sheet
   before the draft begins. Same green bar as Enter Draft on the config page (LoadDraftBtn),
   so the two screens' bottom bars match. */
export const SheetStartBar = styled.div`
  padding: 12px 16px;
  background: #fff;
`;

export const SheetStartBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 14px 32px;
  background: linear-gradient(180deg, #4ade80 0%, #16a34a 100%);
  border: 2px solid #86efac;
  border-radius: 12px;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #fff;
  cursor: pointer;

  &:active { background: linear-gradient(180deg, #22c55e 0%, #15803d 100%); }

  svg {
    width: 14px;
    height: 16px;
  }
`;

export const PausedBanner = styled.div`
  padding: clamp(12px, 0.75vw, 29px) clamp(16px, 1.0vw, 38px);
  display: flex;
  justify-content: center;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
`;

export const ResumeDraftBtn = styled.button`
  display: flex;
  align-items: center;
  gap: clamp(8px, 0.5vw, 19px);
  padding: clamp(8px, 0.5vw, 19px) clamp(24px, 1.5vw, 58px);
  background: #012337;
  color: #fff;
  border: none;
  border-radius: 100px;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 700;
  cursor: pointer;

  &:hover { background: #03111b; }
`;

export const EmptyState = styled.div`
  padding: clamp(32px, 2.0vw, 77px) clamp(16px, 1.0vw, 38px);
  text-align: center;
  font-family: 'Saira Semi Condensed', sans-serif;
  color: #6b7280;
  font-size: clamp(14px, 0.88vw, 34px);
`;

export const PaginationWrap = styled.div`
  padding: clamp(8px, 0.5vw, 19px) clamp(16px, 1.0vw, 38px);
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
`;

/* ── Drafting indicator ───────────────────────────────────────────── */

/* Shown in place of the empty list while the next team's board is being fetched. Only one board
   is loaded at a time, so switching teams leaves a gap — this fills it with the pick that just
   landed rather than an empty panel. */
export const DraftingIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 40px 16px;
`;

export const DraftingSpinner = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 4px solid #d8dde2;
  border-top-color: #8a97a3;
  animation: draftingSpin 0.9s linear infinite;

  @keyframes draftingSpin {
    to { transform: rotate(360deg); }
  }
`;

export const DraftingBody = styled.div`
  min-width: 0;
`;

export const DraftingLabel = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #6b7280;
  line-height: 1.3;
`;

export const DraftingPlayer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #1f2937;
  line-height: 1.2;

  img {
    width: 24px;
    height: 24px;
    object-fit: contain;
    flex-shrink: 0;
  }

  span {
    font-size: 14px;
    font-weight: 700;
    color: #6b7280;
  }
`;

/* ── No picks left ────────────────────────────────────────────────── */

/* Replaces the board once the user has no picks remaining — the list is unusable at that point
   (every Draft button is disabled), so it gives them the only number that still matters: how
   much of the mock draft is left to run. */
export const PicksRemaining = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(14px, 0.88vw, 34px);
  padding: clamp(24px, 1.5vw, 58px) clamp(16px, 1vw, 38px);
  font-family: 'Saira Semi Condensed', sans-serif;
  color: #5b6b7a;
`;

/* The draft is still running while this shows, so a spinner says "in progress" rather than
   "finished" — the count ticks down behind it. */
export const PicksRemainingSpinner = styled.div`
  width: clamp(32px, 2vw, 77px);
  height: clamp(32px, 2vw, 77px);
  border-radius: 50%;
  flex-shrink: 0;
  border: clamp(5px, 0.31vw, 12px) solid #d8dde2;
  border-top-color: #8a97a3;
  animation: picksSpin 0.9s linear infinite;

  @keyframes picksSpin {
    to { transform: rotate(360deg); }
  }
`;

export const PicksRemainingCount = styled.div`
  min-width: clamp(35px, 2.19vw, 84px);
  text-align: center;
  font-size: clamp(30px, 1.88vw, 72px);
  font-weight: 700;
  color: #2f3b47;
  line-height: 1;
`;

export const PicksRemainingLabel = styled.div`
  font-size: clamp(16px, 1vw, 38px);
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
`;
